import get from 'lodash/get';
import isString from 'lodash/isString';
import ExtensionCollection from './ExtensionCollection';
import surveyUrls from './surveyUrls';

function getResourceTags(resource: fhir.Resource): fhir.Coding[] {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const meta = get<fhir.Resource, 'meta', fhir.Meta>(resource, 'meta', null);
  return get<fhir.Meta, 'tag', fhir.Coding[]>(meta, 'tag', []);
}

/**
 * Both surveys and consent leverage QuestionnaireResponses.  This determines if the questionnaireResposne
 * is used in the context of a consent.
 */
function isConsentQuestionnaireResponse(
  questionnaireResponse: fhir.QuestionnaireResponse,
) {
  const tags = getResourceTags(questionnaireResponse);
  return !!tags.find(tag => {
    const system = get<fhir.Coding, 'system'>(tag, 'system');
    const code = get<fhir.Coding, 'code'>(tag, 'code');
    return (
      system === 'http://lifeomic.com/fhir/source' &&
      code === 'LifeOmic Consent'
    );
  });
}

/**
 * Has the user started completing this response
 */
function isResponseNotStarted(
  questionnaireResponse: fhir.QuestionnaireResponse,
) {
  const extensionCollection = ExtensionCollection.initFromBackboneElement(
    questionnaireResponse,
  );
  return extensionCollection.hasExtension(surveyUrls.surveyNotStarted);
}

export type SortableStatus = 'not-started' | 'in-progress' | 'completed';

function sortResponses(
  responses: fhir.QuestionnaireResponse[],
  sortOrder: SortableStatus[] = ['not-started', 'in-progress', 'completed'],
): fhir.QuestionnaireResponse[] {
  const getSortIndex = (response: fhir.QuestionnaireResponse) => {
    const derivedStatus: SortableStatus = isResponseNotStarted(response)
      ? 'not-started'
      : (response.status as SortableStatus);

    const targetIndex = sortOrder.indexOf(derivedStatus);
    if (targetIndex === -1) {
      console.error(`Survey had unrecognized status of: "${response.status}"`);
      return sortOrder.length;
    } else {
      return targetIndex;
    }
  };

  const safeCopied = [...(responses || [])];
  safeCopied.sort(
    (response1, response2) => getSortIndex(response1) - getSortIndex(response2),
  );

  return safeCopied;
}

/**
 * QuestionnaireResponses will reference their associated questionnaire.  This extracts that guid from the resource
 */
function surveyIdFromResponse(
  questionnaireResponse: fhir.QuestionnaireResponse,
) {
  const associatedQuestionnaire = get<
    fhir.QuestionnaireResponse,
    'questionnaire'
  >(questionnaireResponse, 'questionnaire');
  const reference = get<fhir.Reference, 'reference'>(
    associatedQuestionnaire,
    'reference',
  );
  if (!reference || !isString(reference)) return null;

  return reference
    .replace('/lifeomic/dstu3/Questionnaire/', '')
    .replace('Questionnaire/', '');
}

/**
 * Checks if this survey should only be completed by Doctors/Researcher on behalf of a subject
 */
function isInternalSurvey(questionnaire: fhir.Questionnaire) {
  const tags = getResourceTags(questionnaire);
  return !!tags.find(tag => {
    const system = get<fhir.Coding, 'system'>(tag, 'system');
    return system === surveyUrls.internalSurvey;
  });
}

/**
 * Checks if this survey permits additional responses even after it has been completed
 */
function allowsResponseResubmission(questionnaire: fhir.Questionnaire) {
  const tags = getResourceTags(questionnaire);
  return !!tags.find(tag => {
    return (
      tag?.system === surveyUrls.allowsResponseResubmission &&
      tag?.code === 'true'
    );
  });
}

/**
 * Checks if this survey is configured to use a custom vendor ID
 */
function hasCustomVendorId(questionnaire: fhir.Questionnaire) {
  const tags = getResourceTags(questionnaire);
  return tags.some(tag => tag?.system === surveyUrls.vendorId);
}

/**
 * Returns the primary survey ID associated with this survey
 */
function getPrimarySurveyId(questionnaire: fhir.Questionnaire): string | null {
  const tags = getResourceTags(questionnaire);
  const targetTag = tags.find(_ => _?.system === surveyUrls.primarySurveyId);

  return targetTag?.code || null;
}

function hasStatus(
  questionnaireResponse: fhir.QuestionnaireResponse,
  status: string,
) {
  return status === questionnaireResponse?.status;
}

export default {
  isConsentQuestionnaireResponse,
  isResponseNotStarted,
  surveyIdFromResponse,
  isInternalSurvey,
  sortResponses,
  getPrimarySurveyId,
  hasStatus,
  allowsResponseResubmission,
  hasCustomVendorId,
};
